import { useCallback, useEffect, useMemo, useRef } from 'react';

import { cx } from '@hrworks/design-system/functions';

import type { ControlledCollapsibleMenuProps } from './CollapsibleMenu.types';
import { S } from './ControlledCollapsibleMenu.styles';
import {
  ControlledCollapsibleMenuContext,
  type ControlledCollapsibleMenuContextProps,
} from './ControlledCollapsibleMenuContext';

export const ControlledCollapsibleMenu = ({
  children,
  activeEntryId,
  activeEntryPath,
  expandedEntryIds,
  updateExpandedEntryIds,
  updateActiveEntryId,
  multiple,
  showDepthIndicator,
  className,
  ...otherProps
}: ControlledCollapsibleMenuProps) => {
  const isActive = useMemo(
    () => (itemId: string) => {
      return Boolean(activeEntryId === itemId);
    },
    [activeEntryId],
  );

  const isExpanded = useMemo(
    () => (itemId: string) => {
      return Boolean(expandedEntryIds?.includes(itemId));
    },
    [expandedEntryIds],
  );

  const expand = useCallback(
    (newExpandedEntryIds: Array<string>) => {
      if (multiple) {
        // Add only the new IDs to the existing expanded IDs
        updateExpandedEntryIds([...new Set([...expandedEntryIds, ...newExpandedEntryIds])]);
      } else {
        // Only keep active path IDs that are currently expanded
        const activePathCurrentlyExpanded = (activeEntryPath || []).filter((id) => expandedEntryIds.includes(id));
        updateExpandedEntryIds([...new Set([...activePathCurrentlyExpanded, ...newExpandedEntryIds])]);
      }
    },
    [multiple, expandedEntryIds, updateExpandedEntryIds, activeEntryPath],
  );

  const collapse = useCallback(
    (itemId: string) => {
      const newExpandedEntryIds = expandedEntryIds.filter((expandedEntryId) => {
        // Remove the collapsed item and its children
        return expandedEntryId !== itemId;
      });
      updateExpandedEntryIds(newExpandedEntryIds);
    },
    [expandedEntryIds, updateExpandedEntryIds],
  );

  // Skip on first render
  const isFirstRender = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies:  We only want this to fire once multiple changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }
    // If multiple gets changed to false and multiple submenus are open close them
    if (!multiple && expandedEntryIds.length > 1) {
      updateExpandedEntryIds([]);
    }
  }, [multiple]);

  const contextValue = useMemo<ControlledCollapsibleMenuContextProps>(
    () => ({
      isActive,
      isExpanded,
      expand,
      collapse,
      updateActiveEntryId,
      isParentExpanded: true,
      multiple,
    }),
    [collapse, expand, isActive, isExpanded, updateActiveEntryId, multiple],
  );

  return (
    <S.Container
      className={cx(S.cls.Container, className)}
      data-showcollapsiblemenudepthindicator={showDepthIndicator}
      {...otherProps}
    >
      <ControlledCollapsibleMenuContext.Provider value={contextValue}>
        {children}
      </ControlledCollapsibleMenuContext.Provider>
    </S.Container>
  );
};
